import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/AdminCounselingDetail.scss';
import backIcon from '../../assets/left.svg'; // Use appropriate arrow icon
import icon from '../../assets/chat.svg';
import AppSpinner from '../../components/AppSpinner';

interface Message {
  sender: 'user' | 'bot';
  content: string;
  timestamp: string;
}

interface CounselingSessionDetail {
  _id: string;
  user: {
    _id: string;
    displayName: string;
    userId: string;
    email: string;
  };
  messages: Message[];
  updatedAt: string;
  createdAt: string;
}

const AdminCounselingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [session, setSession] = useState<CounselingSessionDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAuthenticated || !id) return;

    const fetchSessionDetail = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/admin/counseling-sessions/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setSession(response.data);
      } catch (err) {
        console.error(err);
        setError('상담 내역 상세 정보를 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchSessionDetail();
  }, [isAuthenticated, id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session]);

  if (loading) return <AppSpinner fullscreen tip="Loading..." />;
  if (error) return <div className="admin-page">에러: {error}</div>;
  if (!session) return <div className="admin-page">상담 내역을 찾을 수 없습니다.</div>;

  return (
    <div className="admin-counseling-detail-page">
      <header className="counseling-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate(-1)}>
            <img src={backIcon} alt="뒤로가기" />
          </button>
          <h1>
            <img src={icon} alt="리본상담" style={{ width: '24px', height: '24px' }} />
            리본상담 상세
          </h1>
        </div>

        <div className="user-info-summary">
          <span>{session.user?.displayName || '알 수 없음'}</span>
          <div className="divider"></div>
          <span>{session.user?.userId || '-'}</span>
          <div className="divider"></div>
          <span>{new Date(session.createdAt).toLocaleDateString()} 시작</span>
        </div>
      </header>

      <div className="chat-interface">
        <div className="message-list">
          {session.messages.map((msg, index) => (
            <div key={index} className={`message-bubble ${msg.sender}`}>
              <p>{msg.content}</p>
              <div className="time"
                style={{
                  fontSize: '11px',
                  color: '#aaa',
                  marginTop: '4px',
                  textAlign: msg.sender === 'user' ? 'right' : 'left',
                }}
              >
                {new Date(msg.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

export default AdminCounselingDetailPage;
